import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ProgressBar } from "@/components/ProgressBar";
import { overallStats, masteryStadioProgress, getProgram, getActiveProgramId } from "@/data/program";
import { LogOut, RotateCcw, Mail, Trophy, AlertTriangle, Repeat } from "lucide-react";

export default function Profile() {
  const { user, completed, logout, resetProgress, stats: cumulativeStats } = useAuth();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const program = getProgram(getActiveProgramId());
  const isMastery = program?.progressionType === "mastery";
  const calStats = overallStats(program, completed);
  const masteryProgress = isMastery ? masteryStadioProgress(program, completed) : null;

  return (
    <div className="px-5 pt-12 animate-slideup">
      <h1 className="font-head text-4xl font-black uppercase tracking-tight text-white">Profilo</h1>

      <div className="mt-6 border border-zinc-800 bg-[#141414] p-5">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center bg-[#fa003f] font-head text-3xl font-black text-white">
            {(user?.name || "A").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="font-head text-2xl uppercase tracking-tight text-white truncate">
              {user?.name}
            </p>
            <p className="flex items-center gap-1.5 text-sm text-zinc-500 truncate">
              <Mail size={13} /> {user?.email}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4 border border-zinc-800 bg-[#141414] p-5">
        <div className="flex items-center gap-2 mb-4">
          <Trophy size={18} className="text-[#fa003f]" />
          <h2 className="font-head text-xl uppercase tracking-wide text-white">Statistiche</h2>
        </div>
        {isMastery && masteryProgress && (
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">
            {masteryProgress.phase.name}
          </p>
        )}
        <div className="grid grid-cols-3 gap-3 text-center mb-4">
          <div>
            <p className="font-head text-3xl font-black text-white">
              {isMastery ? cumulativeStats.workouts : calStats.doneDays}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">
              {isMastery ? "Allenamenti" : "Giorni"}
            </p>
          </div>
          <div>
            <p className="font-head text-3xl font-black text-white">
              {isMastery ? cumulativeStats.exercises : calStats.doneEx}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Esercizi</p>
          </div>
          <div>
            <p className="font-head text-3xl font-black text-[#fa003f]">
              {isMastery ? masteryProgress.pct : calStats.pct}%
            </p>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">
              {isMastery ? "Padronanza" : "Totale"}
            </p>
          </div>
        </div>
        <ProgressBar pct={isMastery ? masteryProgress.pct : calStats.pct} />
      </div>

      <div className="mt-6 space-y-3">
        <button
          data-testid="change-program-btn"
          onClick={() => navigate("/select")}
          className="w-full h-14 flex items-center justify-between gap-2 border border-zinc-800 bg-[#141414] px-4 active:scale-[0.98] transition-transform"
        >
          <span className="flex items-center gap-2 font-head text-lg uppercase tracking-wide text-white">
            <Repeat size={18} className="text-[#fa003f]" /> Cambia programma
          </span>
          <span className="text-xs text-zinc-500 truncate max-w-[40%]">{program?.name}</span>
        </button>

        {!confirm ? (
          <button
            data-testid="reset-progress-btn"
            onClick={() => setConfirm(true)}
            className="w-full h-14 flex items-center justify-center gap-2 border border-zinc-800 font-head text-lg uppercase tracking-wide text-zinc-300 active:scale-[0.98] transition-transform"
          >
            <RotateCcw size={18} /> Azzera progressi
          </button>
        ) : (
          <div className="border border-[#fa003f]/50 bg-[#fa003f]/[0.06] p-4">
            <p className="flex items-center gap-2 text-sm text-zinc-300 mb-3">
              <AlertTriangle size={16} className="text-[#fa003f]" />
              Sicuro? Tutti i progressi verranno cancellati.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirm(false)}
                className="flex-1 h-12 border border-zinc-700 font-head uppercase tracking-wide text-zinc-300"
              >
                Annulla
              </button>
              <button
                data-testid="reset-confirm-btn"
                onClick={() => {
                  resetProgress();
                  setConfirm(false);
                }}
                className="flex-1 h-12 bg-[#fa003f] font-head uppercase tracking-wide text-white"
              >
                Azzera
              </button>
            </div>
          </div>
        )}

        <button
          data-testid="logout-btn"
          onClick={logout}
          className="w-full h-14 flex items-center justify-center gap-2 border border-zinc-800 font-head text-lg uppercase tracking-wide text-zinc-300 active:scale-[0.98] transition-transform"
        >
          <LogOut size={18} /> Esci
        </button>
      </div>

      <p className="mt-8 text-center text-[11px] text-zinc-700 uppercase tracking-widest">
        Mobility Program
      </p>
    </div>
  );
}
